import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useNavigate, useParams } from 'react-router-dom';
import { authService } from '../../services/api';
import { accountsService } from '../../services/accountsService';
import { cardsService } from '../../services/cardsService';
import BooleanPill, { BooleanSelectEditor } from '../Shared/BooleanPill';
import ResourceSidebar from '../Shared/ResourceSidebar';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import '../Venues/VenuesPage.css';
import '../Info/InfoPage.css';
import './AccountsPage.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const vendorOptions = [
  { label: 'Dice', slug: 'dice', apiVendor: 'dice' },
  { label: 'Belly Up Aspen', slug: 'belly-up', apiVendor: 'aspen' },
  { label: 'Pacha', slug: 'pacha', apiVendor: 'pacha' },
  { label: 'TAO', slug: 'tao', apiVendor: 'tao' },
  { label: 'Get-In', slug: 'get-in', apiVendor: 'get_in' },
  { label: 'Seetickets', slug: 'seetickets', apiVendor: 'seetickets' },
  { label: 'Ticketmaster', slug: 'ticketmaster', apiVendor: 'ticketmaster' },
  { label: 'AXS', slug: 'axs', apiVendor: 'axs' },
  { label: 'Tixr', slug: 'tixr', apiVendor: 'tixr' },
];

const accountVendorConfig = {
  dice: {
    label: 'Dice',
    apiVendor: 'dice',
    locationOptions: ['New York', 'Miami', 'Los Angeles', 'Universal', 'Europe', 'London'],
    showInventory: true,
    showSecondaryCard: true,
    showReddit: true,
    cardHeader: 'Primary Card',
  },
  'belly-up': {
    label: 'Belly Up Aspen',
    apiVendor: 'aspen',
    locationOptions: ['Aspen'],
    showInventory: true,
    showSecondaryCard: true,
    showReddit: false,
    showPassword: true,
    showBillingInfo: true,
    showLocation: false,
    showPasswordAfterLogin: true,
    showPrimaryCardAfterPassword: true,
    cardHeader: 'Primary Card',
  },
  pacha: {
    label: 'Pacha',
    apiVendor: 'pacha',
    locationOptions: [],
    showInventory: true,
    showStatus: true,
    showSecondaryCard: false,
    showLocation: false,
    showFullName: true,
    showDateOfBirth: true,
    showPhoneNumber: true,
    phoneNumberEditable: true,
    allowCardDetails: false,
    supportsOverrides: false,
    showUserHistory: true,
    cardHeader: 'Card',
  },
  tao: {
    label: 'Tao',
    apiVendor: 'tao',
    locationOptions: ['Chicago', 'Las Vegas', 'New York', 'Miami', 'Los Angeles'],
    showInventory: true,
    showSecondaryCard: false,
    showReddit: false,
    showPassword: true,
    showTaoApp: true,
    showBillingInfo: true,
    showPrimaryCardAfterPassword: true,
    locationColumnWidth: 112,
    cardHeader: 'Card',
    allowCardDetails: false,
  },
  'get-in': {
    label: 'Get-In',
    apiVendor: 'get_in',
    locationOptions: [],
    showInventory: true,
    showStatus: true,
    showSecondaryCard: true,
    showComment: true,
    showLocation: false,
    allowCardDetails: false,
    supportsOverrides: false,
    showUserHistory: true,
    cardHeader: 'Card',
  },
  seetickets: {
    label: 'Seetickets',
    apiVendor: 'seetickets',
    locationOptions: [],
    showInventory: true,
    showStatus: true,
    showSecondaryCard: true,
    showLocation: true,
    allowCardDetails: true,
    supportsOverrides: true,
    showUserHistory: true,
    cardHeader: 'Primary Card',
  },
  ticketmaster: {
    label: 'Ticketmaster',
    apiVendor: 'ticketmaster',
    locationOptions: [],
    showInventory: true,
    showStatus: true,
    showSecondaryCard: false,
    showLocation: false,
    showPhoneNumber: true,
    phoneNumberEditable: true,
    restrictPhoneNumberEditors: true,
    allowCardDetails: false,
    supportsOverrides: false,
    showUserHistory: true,
    cardHeader: 'Card',
  },
  axs: {
    label: 'AXS',
    apiVendor: 'axs',
    locationOptions: [],
    showInventory: true,
    showStatus: true,
    showSecondaryCard: true,
    showLocation: true,
    showPhoneNumber: true,
    phoneNumberEditable: true,
    restrictPhoneNumberEditors: true,
    allowCardDetails: true,
    supportsOverrides: true,
    showUserHistory: true,
    cardHeader: 'Primary Card',
  },
  tixr: {
    label: 'Tixr',
    apiVendor: 'tixr',
    locationOptions: [],
    showInventory: true,
    showStatus: true,
    showSecondaryCard: false,
    showLocation: true,
    showPassword: true,
    allowCardDetails: false,
    supportsOverrides: false,
    showUserHistory: true,
    cardHeader: 'Card',
  },
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const USERNAME_COL_WIDTH = 78;
const STATUS_COL_WIDTH = 92;
const CONTEXT_MENU_WIDTH = 170;
const CONTEXT_SUBMENU_WIDTH = 165;
const PHONE_EDITOR_EMAILS = new Set(['masterte0@tikwiz.com', 'dimos@tikwiz.com']);
const emptyAccountFilters = { location: '', query: '' };
const EMPTY_CREATE_CARD_FORM = {
  card_name: '',
  status: '',
  is_zen_privacy: false,
  expiry: '',
  ccv: '',
};

const formatEventDate = (value) => {
  if (!value) return 'No date';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getDate()}-${MONTH_NAMES[date.getMonth()]}`;
};

const createHistoryState = (account, result) => ({
  account,
  username: result.username || account?.username || '',
  futureItems: result.future_items || [],
  pastItems: result.past_items || [],
  futureCount: result.future_count || 0,
  pastCount: result.past_count || 0,
});

const createCardDraft = (account) => ({
  primaryCard: account?.primary_card || null,
  secondaryCard: account?.secondary_card || null,
  dirty: false,
});

const formatConflictMessage = (vendorLabel, conflicts) => conflicts
  .map((conflict) => {
    const cardLabel = conflict.card_name || 'This card';
    const usernames = (conflict.usernames || []).join(', ');
    return `${cardLabel} is used in ${vendorLabel} by user(s) ${usernames}.`;
  })
  .join(' ');

const renderCardNameWithWarning = (card) => {
  if (!card?.card_name) return '';
  if (!card.is_shared) return card.card_name;
  const usernames = (card.shared_usernames || []).join(', ');
  return `${card.card_name} !${usernames ? ` (${usernames})` : ''}`;
};

const renderSharedCardCell = (card) => {
  if (!card?.card_name) return '';
  if (!card.is_shared) return card.card_name;
  const usernames = (card.shared_usernames || []).join(', ');
  return (
    <span className="accounts-shared-card-label">
      <span>{card.card_name}</span>
      <span className="accounts-shared-card-flag" title={usernames ? `Shared by ${usernames}` : 'Shared card'}>
        !
      </span>
    </span>
  );
};

const accountBillingFields = [
  { key: 'first_name', label: 'First', minWidth: 86, width: 92, maxWidth: 110 },
  { key: 'last_name', label: 'Last', minWidth: 96, width: 110, maxWidth: 130 },
  { key: 'billing_address', label: 'Address', minWidth: 230, width: 250 },
  { key: 'billing_city', label: 'City', minWidth: 112, width: 128, maxWidth: 165 },
  { key: 'billing_state', label: 'State', minWidth: 68, width: 72, maxWidth: 82 },
  { key: 'billing_postal_code', label: 'Zip', minWidth: 88, width: 98, maxWidth: 112 },
  { key: 'phone_number', label: 'Phone', minWidth: 150, width: 165 },
];

const accountSearchFields = (account) => [
  account?.username,
  account?.name,
  account?.dob,
  account?.phone_number,
  account?.email,
  account?.login,
  account?.primary_card?.card_name,
  account?.secondary_card?.card_name,
  account?.comment,
  account?.tao_billing?.email_address,
  account?.tao_billing?.first_name,
  account?.tao_billing?.last_name,
  account?.tao_billing?.billing_address,
  account?.tao_billing?.billing_city,
  account?.tao_billing?.billing_state,
  account?.tao_billing?.billing_postal_code,
  account?.tao_billing?.phone_number,
];

const AccountsPage = () => {
  const navigate = useNavigate();
  const { vendorSlug } = useParams();
  const selectedVendor = vendorOptions.find((vendor) => vendor.slug === vendorSlug);
  const activeAccountVendor = accountVendorConfig[vendorSlug];
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [includeOverrides, setIncludeOverrides] = useState(false);
  const [showRtuOnly, setShowRtuOnly] = useState(false);
  const [accountFilters, setAccountFilters] = useState(emptyAccountFilters);
  const [appliedAccountFilters, setAppliedAccountFilters] = useState(emptyAccountFilters);
  const [cardModal, setCardModal] = useState(null);
  const [cardDetailsDraft, setCardDetailsDraft] = useState('');
  const [savingCardDetails, setSavingCardDetails] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [cardSearchQuery, setCardSearchQuery] = useState('');
  const [cardSuggestions, setCardSuggestions] = useState([]);
  const [cardSuggestionsOpen, setCardSuggestionsOpen] = useState(false);
  const [cardSuggestionsLoading, setCardSuggestionsLoading] = useState(false);
  const [selectedCardSuggestion, setSelectedCardSuggestion] = useState(null);
  const [managingCards, setManagingCards] = useState(false);
  const [cardDraft, setCardDraft] = useState(null);
  const [cardConflictModal, setCardConflictModal] = useState(null);
  const [showCreateCardForm, setShowCreateCardForm] = useState(false);
  const [createCardForm, setCreateCardForm] = useState(EMPTY_CREATE_CARD_FORM);
  const [creatingCard, setCreatingCard] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const accountRequestRef = useRef({ id: 0, controller: null });

  useEffect(() => {
    let isMounted = true;
    const token = authService.getToken();
    if (!token) return undefined;

    authService.getCurrentUser(token)
      .then((user) => {
        if (isMounted) {
          setCurrentUserEmail(String(user?.email || '').trim().toLowerCase());
        }
      })
      .catch(() => {
        if (isMounted) setCurrentUserEmail('');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const loadAccounts = useCallback(async (filters = appliedAccountFilters) => {
    if (!activeAccountVendor) return;
    accountRequestRef.current.controller?.abort();
    const requestId = accountRequestRef.current.id + 1;
    const controller = new AbortController();
    accountRequestRef.current = { id: requestId, controller };

    setLoading(true);
    setError('');
    const result = await accountsService.listVendorAccounts(activeAccountVendor.apiVendor, {
      includeClosed: includeOverrides,
      includeIssue: includeOverrides,
      rtuOnly: showRtuOnly,
      location: filters.location,
      q: filters.query,
      signal: controller.signal,
    });

    if (controller.signal.aborted || accountRequestRef.current.id !== requestId) {
      return;
    }

    if (result.success) {
      setAccounts(result.items || []);
      setTotalCount(result.totalCount || 0);
    } else {
      setError(result.error);
    }
    setLoading(false);
  }, [activeAccountVendor, appliedAccountFilters, includeOverrides, showRtuOnly]);

  useEffect(() => {
    if (activeAccountVendor) {
      loadAccounts();
    } else {
      setAccounts([]);
      setTotalCount(0);
    }

    return () => {
      accountRequestRef.current.controller?.abort();
      accountRequestRef.current = {
        id: accountRequestRef.current.id + 1,
        controller: null,
      };
    };
  }, [activeAccountVendor, loadAccounts]);

  const accountMatchesAppliedFilters = useCallback((account) => {
    if (!account) return false;
    if (!includeOverrides && (account.status === 'Closed' || account.status === 'Issue')) {
      return false;
    }
    if (showRtuOnly && account.status !== 'RTU') {
      return false;
    }
    if (appliedAccountFilters.location && account.location !== appliedAccountFilters.location) {
      return false;
    }

    const query = appliedAccountFilters.query.trim().toLowerCase();
    if (!query) {
      return true;
    }

    return accountSearchFields(account).some((value) => (
      String(value || '').toLowerCase().includes(query)
    ));
  }, [appliedAccountFilters, includeOverrides, showRtuOnly]);

  const updateAccountRow = useCallback((item) => {
    setAccounts((current) => {
      if (!accountMatchesAppliedFilters(item)) {
        return current.filter((account) => account.user_id !== item.user_id);
      }
      if (current.some((account) => account.user_id === item.user_id)) {
        return current.map((account) => (account.user_id === item.user_id ? item : account));
      }
      return [...current, item].sort((left, right) => (
        String(left.username || '').localeCompare(String(right.username || ''))
      ));
    });
  }, [accountMatchesAppliedFilters]);

  const handleAccountFilterSubmit = (event) => {
    event.preventDefault();
    const nextFilters = {
      location: accountFilters.location,
      query: accountFilters.query.trim(),
    };
    const filtersChanged = (
      nextFilters.location !== appliedAccountFilters.location ||
      nextFilters.query !== appliedAccountFilters.query
    );
    if (filtersChanged) {
      setAppliedAccountFilters(nextFilters);
    } else {
      loadAccounts(nextFilters);
    }
  };

  const updateAccountFilter = (field, value) => {
    setAccountFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const formatCurrency = (value) => {
    const amount = Number(value || 0);
    return amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const openCardModal = (account, slot) => {
    const card = account[slot === 'primary' ? 'primary_card' : 'secondary_card'];
    if (!card) return;
    setCardModal({ account, slot, card });
    setCardDetailsDraft(card.details || '');
  };

  const closeCardModal = () => {
    if (savingCardDetails) return;
    setCardModal(null);
    setCardDetailsDraft('');
  };

  const saveCardDetails = async () => {
    if (!cardModal) return;
    setSavingCardDetails(true);
    setError('');
    const result = await accountsService.updateVendorCardDetails(
      activeAccountVendor.apiVendor,
      cardModal.account.user_id,
      cardModal.slot,
      cardDetailsDraft.trim() || null,
    );
    setSavingCardDetails(false);

    if (!result.success) {
      setError(result.error);
      return;
    }
    updateAccountRow(result.item);
    setMessage(result.message || 'Card details updated.');
    setCardModal(null);
  };

  const handleOverride = async (statusOverride) => {
    if (!contextMenu?.account) return;
    setError('');
    const result = await accountsService.updateVendorOverride(
      activeAccountVendor.apiVendor,
      contextMenu.account.user_id,
      statusOverride,
    );
    setContextMenu(null);

    if (!result.success) {
      setError(result.error);
      return;
    }
    updateAccountRow(result.item);
    setMessage(result.message || 'Override updated.');
  };

  const openUserHistory = async (account) => {
    if (!account?.user_id) return;
    setContextMenu(null);
    setHistoryModal({
      username: account.username || '',
      futureItems: [],
      pastItems: [],
      futureCount: 0,
      pastCount: 0,
    });
    setHistoryLoading(true);
    setHistoryError('');
    setError('');

    const result = await accountsService.getVendorUserHistory(activeAccountVendor.apiVendor, account.user_id);
    setHistoryLoading(false);
    if (!result.success) {
      setHistoryError(result.error);
      return;
    }

    setHistoryModal(createHistoryState(account, result));
  };

  const closeHistoryModal = () => {
    setHistoryModal(null);
    setHistoryError('');
    setHistoryLoading(false);
    setCardSearchQuery('');
    setCardSuggestions([]);
    setCardSuggestionsOpen(false);
    setCardSuggestionsLoading(false);
    setSelectedCardSuggestion(null);
    setManagingCards(false);
    setCardDraft(null);
    setCardConflictModal(null);
    setShowCreateCardForm(false);
    setCreateCardForm(EMPTY_CREATE_CARD_FORM);
    setCreatingCard(false);
  };

  const normalizeOptionalText = (value) => {
    const trimmed = String(value || '').trim();
    return trimmed || null;
  };

  useEffect(() => {
    const query = cardSearchQuery.trim();
    if (!historyModal || !query || selectedCardSuggestion?.card_name === query) {
      setCardSuggestions([]);
      setCardSuggestionsOpen(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setCardSuggestionsLoading(true);
      const result = await accountsService.searchCards(query, 8);
      setCardSuggestionsLoading(false);
      if (result.success) {
        setCardSuggestions(result.items || []);
        setCardSuggestionsOpen(true);
      } else {
        setCardSuggestions([]);
        setCardSuggestionsOpen(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [cardSearchQuery, historyModal, selectedCardSuggestion]);

  const selectCardSuggestion = (card) => {
    setSelectedCardSuggestion(card);
    setCardSearchQuery(card.card_name || '');
    setCardSuggestions([]);
    setCardSuggestionsOpen(false);
  };

  const handleCardSearchChange = (value) => {
    setCardSearchQuery(value);
    setSelectedCardSuggestion((current) => (
      current && current.card_name === value ? current : null
    ));
  };

  const applyDraftAddCard = () => {
    if (!selectedCardSuggestion?.card_id) return;
    setCardDraft((current) => {
      if (!current) return current;
      const selected = {
        card_id: selectedCardSuggestion.card_id,
        card_name: selectedCardSuggestion.card_name,
        status: selectedCardSuggestion.status,
        expiry: selectedCardSuggestion.expiry,
        is_zen_privacy: !!selectedCardSuggestion.is_zen_privacy,
        details: null,
      };
      if (current.primaryCard?.card_id === selected.card_id || current.secondaryCard?.card_id === selected.card_id) {
        return current;
      }
      if (!current.primaryCard) {
        return { ...current, primaryCard: selected, dirty: true };
      }
      if (activeAccountVendor?.showSecondaryCard === false) {
        return current;
      }
      if (!current.secondaryCard) {
        return {
          ...current,
          primaryCard: selected,
          secondaryCard: current.primaryCard,
          dirty: true,
        };
      }
      return current;
    });
    setCardSearchQuery('');
    setCardSuggestions([]);
    setCardSuggestionsOpen(false);
    setSelectedCardSuggestion(null);
  };

  const applyDraftSwapCards = () => {
    setCardDraft((current) => {
      if (!current?.primaryCard || !current?.secondaryCard) return current;
      return {
        ...current,
        primaryCard: current.secondaryCard,
        secondaryCard: current.primaryCard,
        dirty: true,
      };
    });
  };

  const applyDraftRemoveCard = (slot) => {
    setCardDraft((current) => {
      if (!current) return current;
      if (slot === 'primary') {
        return {
          ...current,
          primaryCard: current.secondaryCard || null,
          secondaryCard: null,
          dirty: true,
        };
      }
      return {
        ...current,
        secondaryCard: null,
        dirty: true,
      };
    });
  };

  const saveCardDraft = async (confirmSharedCards = false) => {
    if (!historyModal?.account || !activeAccountVendor || !cardDraft) return;
    setManagingCards(true);
    setError('');
    setHistoryError('');
    const result = await accountsService.manageVendorCards(
      activeAccountVendor.apiVendor,
      historyModal.account.user_id,
      {
        primary_card_id: cardDraft.primaryCard?.card_id || null,
        secondary_card_id: cardDraft.secondaryCard?.card_id || null,
        confirm_shared_cards: confirmSharedCards,
      },
    );
    setManagingCards(false);

    if (!result.success) {
      setHistoryError(result.error);
      return;
    }

    if (result.confirmationRequired) {
      setCardConflictModal({
        conflicts: result.conflicts || [],
        message: formatConflictMessage(activeAccountVendor.label, result.conflicts || []),
      });
      return;
    }

    if (!result.item) {
      setHistoryError('Failed to save card changes.');
      return;
    }

    setCardConflictModal(null);
    updateAccountRow(result.item);
    await loadAccounts();
    setMessage(result.message || 'Card management updated.');
    closeHistoryModal();
  };

  const handleManageCards = async () => {
    await saveCardDraft(false);
  };

  const confirmManageCards = async () => {
    await saveCardDraft(true);
  };

  const updateCreateCardForm = (field, value) => {
    setCreateCardForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const canCreateCardForDraft = () => {
    if (!cardDraft) return false;
    if (activeAccountVendor?.showSecondaryCard === false) {
      return !cardDraft.primaryCard;
    }
    return !(cardDraft.primaryCard && cardDraft.secondaryCard);
  };

  const createAndAssignCard = async (event) => {
    event.preventDefault();
    if (!historyModal?.account || !activeAccountVendor || !cardDraft) return;

    const cardName = createCardForm.card_name.trim();
    const ownerUsername = historyModal.account.username || '';
    if (!cardName) {
      setHistoryError('Card Name is required.');
      return;
    }
    if (!ownerUsername) {
      setHistoryError('This account has no username to assign to the card.');
      return;
    }
    if (!canCreateCardForDraft()) {
      setHistoryError('Remove a card before creating another one for this account.');
      return;
    }

    setCreatingCard(true);
    setManagingCards(true);
    setHistoryError('');
    setError('');

    const vendorKey = activeAccountVendor.apiVendor;
    const createPayload = {
      card_name: cardName,
      owner_username: ownerUsername,
      [vendorKey]: ownerUsername,
      status: normalizeOptionalText(createCardForm.status),
      is_zen_privacy: createCardForm.is_zen_privacy,
      expiry: normalizeOptionalText(createCardForm.expiry),
      ccv: normalizeOptionalText(createCardForm.ccv),
    };
    const createResult = await cardsService.createCard(createPayload);
    if (!createResult.success) {
      setHistoryError(createResult.error);
      setCreatingCard(false);
      setManagingCards(false);
      return;
    }

    const createdCard = createResult.item;
    const nextPrimaryCardId = createdCard.card_id;
    const nextSecondaryCardId = activeAccountVendor.showSecondaryCard === false
      ? null
      : (cardDraft.primaryCard?.card_id || null);

    const assignResult = await accountsService.manageVendorCards(
      activeAccountVendor.apiVendor,
      historyModal.account.user_id,
      {
        primary_card_id: nextPrimaryCardId,
        secondary_card_id: nextSecondaryCardId,
        confirm_shared_cards: true,
      },
    );

    setCreatingCard(false);
    setManagingCards(false);

    if (!assignResult.success) {
      setHistoryError(assignResult.error);
      return;
    }
    if (!assignResult.item) {
      setHistoryError('Card created, but failed to assign it to this account.');
      return;
    }

    updateAccountRow(assignResult.item);
    await loadAccounts();
    setMessage(assignResult.message || createResult.message || 'Card created and assigned.');
    closeHistoryModal();
  };

  useEffect(() => {
    if (historyModal?.account) {
      setCardDraft(createCardDraft(historyModal.account));
    }
  }, [historyModal?.account]);

  const copyText = useCallback(async (value, label = 'Value') => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setMessage(`${label} copied.`);
      setError('');
    } catch (clipboardError) {
      setError(`Failed to copy ${label.toLowerCase()}.`);
    }
  }, []);

  const columnDefs = useMemo(() => {
    const isPasswordEditable = activeAccountVendor?.apiVendor === 'tao';
    const passwordColumn = {
      headerName: 'Password',
      field: 'password',
      minWidth: 150,
      editable: isPasswordEditable,
      singleClickEdit: isPasswordEditable,
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        maxLength: 255,
      },
      cellClass: isPasswordEditable ? 'dice-login-cell' : 'accounts-copyable-cell',
      valueFormatter: isPasswordEditable ? (params) => params.value || '-' : undefined,
      cellRenderer: isPasswordEditable ? undefined : (params) => (
        <button
          className="accounts-copy-cell"
          type="button"
          disabled={!params.value}
          onClick={(event) => {
            event.stopPropagation();
            copyText(params.value, 'Password');
          }}
          title={params.value ? 'Copy Password' : 'Password missing'}
        >
          {params.value || '-'}
        </button>
      ),
    };

    const primaryCardColumn = {
      headerName: activeAccountVendor?.cardHeader || 'Primary Card',
      field: 'primary_card.card_name',
      minWidth: 220,
      valueGetter: (params) => params.data?.primary_card?.card_name || '',
      cellRenderer: (params) => renderSharedCardCell(params.data?.primary_card),
      cellClass: 'dice-card-cell',
      tooltipValueGetter: (params) => {
        const card = params.data?.primary_card;
        if (!card?.is_shared) return card?.card_name || '';
        return `${card.card_name} is shared by ${card.shared_usernames.join(', ')}`;
      },
    };

    const cols = [{
      headerName: 'User',
      field: 'username',
      minWidth: USERNAME_COL_WIDTH,
      width: USERNAME_COL_WIDTH,
      maxWidth: USERNAME_COL_WIDTH,
      pinned: 'left',
    }];

    if (activeAccountVendor?.showStatus !== false) {
      cols.push({
        headerName: 'Status',
        field: 'status',
        minWidth: STATUS_COL_WIDTH,
        width: STATUS_COL_WIDTH,
        maxWidth: STATUS_COL_WIDTH,
        cellClassRules: {
          'dice-status-full': (params) => params.value === 'FULL',
          'dice-status-rtu': (params) => params.value === 'RTU',
          'dice-status-atu': (params) => params.value === 'ATU',
          'dice-status-issue': (params) => params.value === 'Issue',
          'dice-status-closed': (params) => params.value === 'Closed',
        },
      });
    }

    if (activeAccountVendor?.showInventory !== false) {
      cols.push({
        headerName: 'Inventory',
        field: 'inventory',
        minWidth: 96,
        width: 104,
        valueFormatter: (params) => formatCurrency(params.value),
        type: 'numericColumn',
        cellClass: 'dice-inventory-cell',
        cellClassRules: {
          'dice-inventory-full': (params) => Number(params.value || 0) > 500,
          'dice-inventory-rtu': (params) => Number(params.value || 0) === 0,
          'dice-inventory-atu': (params) => Number(params.value || 0) > 0 && Number(params.value || 0) < 500,
        },
      });
    }

    cols.push({
      headerName: 'Login',
      field: 'login',
      minWidth: 190,
      cellClass: 'dice-login-cell',
      cellRenderer: (params) => (
        <button
          className="dice-login-copy"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            copyText(params.value, 'Login');
          }}
          title="Copy login"
        >
          {params.value || '-'}
        </button>
      ),
    });

    if (activeAccountVendor?.showFullName) {
      cols.push({
        headerName: 'First Last Name',
        field: 'name',
        minWidth: 190,
        width: 210,
        editable: false,
      });
    }

    if (activeAccountVendor?.showDateOfBirth) {
      cols.push({
        headerName: 'Date of Birth',
        field: 'dob',
        minWidth: 130,
        width: 140,
        editable: false,
        cellDataType: 'dateString',
      });
    }

    if (activeAccountVendor?.showPhoneNumber) {
      const phoneNumberEditable = activeAccountVendor.phoneNumberEditable === true && (
        activeAccountVendor.restrictPhoneNumberEditors !== true
        || PHONE_EDITOR_EMAILS.has(currentUserEmail)
      );
      cols.push({
        headerName: 'Phone Number',
        field: 'phone_number',
        minWidth: 155,
        width: 170,
        editable: phoneNumberEditable,
        singleClickEdit: phoneNumberEditable,
        cellEditor: phoneNumberEditable ? 'agTextCellEditor' : undefined,
        cellEditorParams: phoneNumberEditable ? {
          maxLength: 255,
        } : undefined,
        cellClass: phoneNumberEditable ? 'dice-login-cell' : 'accounts-copyable-cell',
        cellRenderer: phoneNumberEditable ? undefined : (params) => (
          <button
            className="accounts-copy-cell"
            type="button"
            disabled={!params.value}
            onClick={(event) => {
              event.stopPropagation();
              copyText(params.value, 'Phone number');
            }}
            title={params.value ? 'Copy phone number' : 'Phone number missing'}
          >
            {params.value || '-'}
          </button>
        ),
      });
    }

    if (activeAccountVendor?.showPassword && activeAccountVendor?.showPasswordAfterLogin) {
      cols.push(passwordColumn);
    }

    if (activeAccountVendor?.showPrimaryCardAfterPassword && activeAccountVendor?.showPasswordAfterLogin) {
      cols.push(primaryCardColumn);
    }

    if (activeAccountVendor?.showLocation !== false) {
      const locationWidth = activeAccountVendor?.locationColumnWidth || 140;
      const locationOptions = activeAccountVendor?.locationOptions || [];
      cols.push({
        headerName: 'Location',
        field: 'location',
        minWidth: locationWidth,
        width: locationWidth,
        maxWidth: activeAccountVendor?.locationColumnWidth ? locationWidth + 16 : undefined,
        editable: true,
        cellEditor: locationOptions.length ? 'agSelectCellEditor' : 'agTextCellEditor',
        cellEditorParams: locationOptions.length ? { values: locationOptions } : undefined,
      });
    }

    if (activeAccountVendor?.showTaoApp) {
      cols.push({
        headerName: 'Tao App',
        field: 'tao_app',
        minWidth: 90,
        maxWidth: 90,
        editable: true,
        cellEditor: BooleanSelectEditor,
        cellClass: 'accounts-boolean-cell',
        cellRenderer: (params) => <BooleanPill value={params.value} />,
        valueParser: (params) => (
          params.newValue === true
          || String(params.newValue || '').trim().toLowerCase() === 'yes'
          || String(params.newValue || '').trim().toLowerCase() === 'true'
        ),
      });
    }

    if (activeAccountVendor?.showPassword && !activeAccountVendor?.showPasswordAfterLogin) {
      cols.push(passwordColumn);
    }

    if (activeAccountVendor?.showPrimaryCardAfterPassword && !activeAccountVendor?.showPasswordAfterLogin) {
      cols.push(primaryCardColumn);
    }

    if (activeAccountVendor?.showBillingInfo) {
      accountBillingFields.forEach((field) => {
        cols.push({
          headerName: field.label,
          field: `tao_billing.${field.key}`,
          minWidth: field.minWidth,
          width: field.width,
          maxWidth: field.maxWidth,
          valueGetter: (params) => params.data?.tao_billing?.[field.key] || '',
          cellClass: 'accounts-copyable-cell',
          cellRenderer: (params) => (
            <button
              className="accounts-copy-cell"
              type="button"
              disabled={!params.value}
              onClick={(event) => {
                event.stopPropagation();
                copyText(params.value, field.label);
              }}
              title={params.value ? `Copy ${field.label}` : `${field.label} missing`}
            >
              {params.value || '-'}
            </button>
          ),
        });
      });
    }

    if (!activeAccountVendor?.showPrimaryCardAfterPassword) {
      cols.push(primaryCardColumn);
    }

    if (activeAccountVendor?.showSecondaryCard !== false) {
      cols.push({
        headerName: 'Secondary Card',
        field: 'secondary_card.card_name',
        minWidth: 220,
        valueGetter: (params) => params.data?.secondary_card?.card_name || '',
        cellRenderer: (params) => renderSharedCardCell(params.data?.secondary_card),
        cellClass: 'dice-card-cell',
        tooltipValueGetter: (params) => {
          const card = params.data?.secondary_card;
          if (!card?.is_shared) return card?.card_name || '';
          return `${card.card_name} is shared by ${card.shared_usernames.join(', ')}`;
        },
      });
    }

    if (activeAccountVendor?.showReddit) {
      cols.push({
        headerName: 'Reddit',
        field: 'reddit',
        minWidth: 74,
        width: 74,
        maxWidth: 74,
        cellClass: 'dice-reddit-cell',
      });
    }

    if (activeAccountVendor?.showComment) {
      cols.push({
        headerName: 'Comment',
        field: 'comment',
        minWidth: 240,
        flex: 1,
        editable: true,
        singleClickEdit: true,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: {
          maxLength: 2000,
          rows: 6,
          cols: 50,
        },
        tooltipField: 'comment',
      });
    }

    return cols;
  }, [activeAccountVendor, copyText, currentUserEmail]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    floatingFilter: true,
    resizable: true,
  }), []);

  const onCellClicked = (params) => {
    if (params.event?.detail > 1) {
      return;
    }
    if (activeAccountVendor?.allowCardDetails === false) {
      return;
    }
    if (params.colDef.field === 'primary_card.card_name') {
      openCardModal(params.data, 'primary');
    }
    if (params.colDef.field === 'secondary_card.card_name') {
      openCardModal(params.data, 'secondary');
    }
  };

  const onCellContextMenu = (params) => {
    params.event.preventDefault();
    const submenuDirection = params.event.clientX + CONTEXT_MENU_WIDTH + CONTEXT_SUBMENU_WIDTH > window.innerWidth
      ? 'left'
      : 'right';

    setContextMenu({
      account: params.data,
      submenuDirection,
      x: params.event.clientX,
      y: params.event.clientY,
    });
  };

  const onCellDoubleClicked = (params) => {
    if (activeAccountVendor?.showUserHistory === false) {
      return;
    }
    if (params.colDef?.field === 'comment' || params.colDef?.field === 'phone_number') {
      return;
    }
    if (activeAccountVendor?.apiVendor === 'tao' && params.colDef?.field === 'password') {
      return;
    }
    openUserHistory(params.data);
  };

  const onCellValueChanged = async (params) => {
    const { data, colDef, oldValue, newValue, node } = params;
    if (oldValue === newValue) {
      return;
    }

    let result = null;
    let fallbackValue = oldValue;
    if (colDef.field === 'location') {
      result = await accountsService.updateVendorLocation(activeAccountVendor.apiVendor, data.user_id, newValue || null);
    } else if (colDef.field === 'phone_number') {
      const nextValue = newValue == null ? null : String(newValue).trim() || null;
      fallbackValue = oldValue || null;
      result = await accountsService.updateVendorPhoneNumber(activeAccountVendor.apiVendor, data.user_id, nextValue);
    } else if (colDef.field === 'comment') {
      const nextValue = newValue == null ? null : String(newValue).trim() || null;
      fallbackValue = oldValue || null;
      result = await accountsService.updateVendorComment(activeAccountVendor.apiVendor, data.user_id, nextValue);
    } else if (colDef.field === 'tao_app') {
      const nextValue = newValue === true || String(newValue || '').trim().toLowerCase() === 'yes' || String(newValue || '').trim().toLowerCase() === 'true';
      fallbackValue = !!oldValue;
      result = await accountsService.updateTaoApp(data.user_id, nextValue);
    } else if (colDef.field === 'password' && activeAccountVendor?.apiVendor === 'tao') {
      const nextValue = newValue == null ? null : String(newValue).trim() || null;
      fallbackValue = oldValue || null;
      result = await accountsService.updateTaoPassword(data.user_id, nextValue);
    } else {
      return;
    }

    if (!result.success) {
      node.setData({
        ...data,
        [colDef.field]: fallbackValue,
      });
      setError(result.error);
      return;
    }

    updateAccountRow(result.item);
    if (accountMatchesAppliedFilters(result.item)) {
      node.setData(result.item);
    }
    setError('');
    setMessage(result.message || 'Account updated.');
  };

  return (
    <div className="venues-page resource-page-with-sidebar info-page accounts-page" onClick={() => setContextMenu(null)}>
      <ResourceSidebar section="accounts" />
      <header className="venues-header">
        <div className="venues-header-content">
          <HeaderNewTabButton />
          <h1>{selectedVendor ? `${selectedVendor.label} Accounts` : 'Accounts'}</h1>
          <div className="venues-header-actions">
            <button className="btn btn-secondary" type="button" onClick={() => navigate('/user')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="venues-main">
        {!activeAccountVendor && (
          <div className="accounts-placeholder">
            Select a vendor from the Accounts menu to view vendor account data.
          </div>
        )}

        {activeAccountVendor && (
          <>
            <form className="accounts-filter-form" onSubmit={handleAccountFilterSubmit}>
              {activeAccountVendor.showLocation !== false && (
                <div className="form-group">
                  <label htmlFor="accounts-location">Location</label>
                  {(activeAccountVendor.locationOptions || []).length ? (
                    <select
                      id="accounts-location"
                      value={accountFilters.location}
                      onChange={(event) => updateAccountFilter('location', event.target.value)}
                    >
                      <option value="">All</option>
                      {(activeAccountVendor.locationOptions || []).map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id="accounts-location"
                      type="text"
                      value={accountFilters.location}
                      onChange={(event) => updateAccountFilter('location', event.target.value)}
                      placeholder="Filter by location..."
                    />
                  )}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="accounts-query">Search</label>
                <input
                  id="accounts-query"
                  type="text"
                  value={accountFilters.query}
                  onChange={(event) => updateAccountFilter('query', event.target.value)}
                  placeholder="Account, Card, Email..."
                />
              </div>
              <div className="accounts-filter-actions">
                <button className="btn btn-primary accounts-apply-btn" type="submit">
                  Apply
                </button>
                {activeAccountVendor.supportsOverrides !== false && (
                  <button
                    className={`btn ${includeOverrides ? 'btn-primary' : 'btn-secondary'} accounts-filter-toggle accounts-filter-toggle-closed`}
                    type="button"
                    onClick={() => setIncludeOverrides((current) => !current)}
                  >
                    Show Closed/Issue
                  </button>
                )}
                {activeAccountVendor.showInventory !== false && (
                  <button
                    className={`btn ${showRtuOnly ? 'btn-primary' : 'btn-secondary'} accounts-filter-toggle accounts-filter-toggle-rtu`}
                    type="button"
                    onClick={() => setShowRtuOnly((current) => !current)}
                  >
                    Show RTU
                  </button>
                )}
              </div>
            </form>

            <div className="venues-summary">
              <strong>Showing:</strong> {accounts.length} {activeAccountVendor.label} accounts
              {!!totalCount && <span className="venues-summary-tag">Total: {totalCount}</span>}
              {activeAccountVendor.showLocation !== false && appliedAccountFilters.location && <span className="venues-summary-tag">Location: {appliedAccountFilters.location}</span>}
              {appliedAccountFilters.query && <span className="venues-summary-tag">Search: {appliedAccountFilters.query}</span>}
              {activeAccountVendor.supportsOverrides !== false && (
                <span className="venues-summary-tag">{includeOverrides ? 'Closed/Issue included' : 'Closed/Issue filtered out'}</span>
              )}
              {activeAccountVendor.showInventory !== false && showRtuOnly && <span className="venues-summary-tag">RTU only</span>}
            </div>

            {error && <div className="notification error">{error}</div>}
            {message && <div className="notification success">{message}</div>}

            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading {activeAccountVendor.label} accounts...</p>
              </div>
            ) : (
              <div className="venues-grid-shell">
                <div className="ag-theme-quartz-dark venues-ag-grid accounts-ag-grid">
                  <AgGridReact
                    theme="legacy"
                    rowData={accounts}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    animateRows={true}
                    alwaysShowHorizontalScroll={true}
                    pagination={true}
                    paginationPageSize={100}
                    getRowId={(params) => params.data.user_id}
                    onCellClicked={onCellClicked}
                    onCellDoubleClicked={onCellDoubleClicked}
                    onCellContextMenu={onCellContextMenu}
                    preventDefaultOnContextMenu
                    onCellValueChanged={onCellValueChanged}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {contextMenu && (
        <div
          className={`accounts-context-menu accounts-context-menu-${contextMenu.submenuDirection || 'right'}`}
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(event) => event.stopPropagation()}
        >
          {activeAccountVendor?.showUserHistory !== false && (
            <button type="button" onClick={() => openUserHistory(contextMenu.account)}>User History</button>
          )}
          {activeAccountVendor?.supportsOverrides !== false && (
            <div className="accounts-context-parent">
              <button className="accounts-context-parent-trigger" type="button">
                <span>Override Status</span>
                <span>&rsaquo;</span>
              </button>
              <div className="accounts-context-submenu">
                <button type="button" onClick={() => handleOverride('Closed')}>Closed</button>
                <button type="button" onClick={() => handleOverride('Issue')}>Issue</button>
                <button type="button" onClick={() => handleOverride(null)}>Clear Override</button>
              </div>
            </div>
          )}
        </div>
      )}

      {(historyModal || historyLoading || historyError) && (
        <div className="venues-modal-backdrop" onMouseDown={closeHistoryModal}>
          <section className="venues-modal accounts-history-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="venues-modal-header">
              <div>
                <h2>User History</h2>
                {historyModal?.username ? <span className="accounts-history-subtitle">{historyModal.username}</span> : null}
              </div>
              <button className="venues-modal-close" type="button" onClick={closeHistoryModal}>
                x
              </button>
            </div>
            <div className="accounts-history-body">
              {historyLoading ? (
                <div className="accounts-history-empty">Loading user history...</div>
              ) : null}
              {!historyLoading && historyError ? (
                <div className="accounts-history-empty accounts-history-error">{historyError}</div>
              ) : null}
              {!historyLoading && !historyError && historyModal ? (
                <>
                  <section className="accounts-history-section">
                    <header>
                      <h3>Future Events</h3>
                      <span>{historyModal.futureCount} events</span>
                    </header>
                    {!historyModal.futureItems.length ? (
                      <div className="accounts-history-empty">No future events assigned to this user.</div>
                    ) : (
                      <div className="accounts-history-list">
                        {historyModal.futureItems.map((item) => (
                          <article key={item.event_id} className="accounts-history-card">
                            <div className="accounts-history-card-top">
                              <span>{item.event_ref || item.source_uuid || '-'}</span>
                              <strong>{formatEventDate(item.event_date)}</strong>
                            </div>
                            <h4>{item.event_name}</h4>
                            <p>{[item.artist, item.ticket_type, item.venue, item.location].filter(Boolean).join(' | ')}</p>
                          </article>
                        ))}
                      </div>
                    )}
                  </section>
                  <section className="accounts-history-section">
                    <header>
                      <h3>Past Events</h3>
                      <span>{historyModal.pastCount} events</span>
                    </header>
                    {!historyModal.pastItems.length ? (
                      <div className="accounts-history-empty">No past events assigned to this user.</div>
                    ) : (
                      <div className="accounts-history-list">
                        {historyModal.pastItems.map((item) => (
                          <article key={item.event_id} className="accounts-history-card">
                            <div className="accounts-history-card-top">
                              <span>{item.event_ref || item.source_uuid || '-'}</span>
                              <strong>{formatEventDate(item.event_date)}</strong>
                            </div>
                            <h4>{item.event_name}</h4>
                            <p>{[item.artist, item.ticket_type, item.venue, item.location].filter(Boolean).join(' | ')}</p>
                          </article>
                        ))}
                      </div>
                    )}
                  </section>
                  <section className="accounts-history-section accounts-card-management">
                    <header>
                      <h3>Card Management</h3>
                      <span>{activeAccountVendor?.label || 'Account'}</span>
                    </header>
                    <div className="accounts-card-management-grid">
                        <div className="accounts-card-management-slot">
                          <span>Primary</span>
                        <strong>{renderCardNameWithWarning(cardDraft?.primaryCard) || 'None'}</strong>
                      </div>
                      {activeAccountVendor?.showSecondaryCard !== false ? (
                        <div className="accounts-card-management-slot">
                          <span>Secondary</span>
                          <strong>{renderCardNameWithWarning(cardDraft?.secondaryCard) || 'None'}</strong>
                        </div>
                      ) : null}
                    </div>

                    {historyError ? (
                      <div className="accounts-history-empty accounts-history-error">{historyError}</div>
                    ) : null}

                    <div className="form-group accounts-card-search">
                      <label htmlFor="accounts-card-search">Add Card</label>
                      <input
                        id="accounts-card-search"
                        type="text"
                        value={cardSearchQuery}
                        onChange={(event) => handleCardSearchChange(event.target.value)}
                        placeholder="Search by card name..."
                        disabled={managingCards}
                        autoComplete="off"
                      />
                      {cardSuggestionsLoading ? <div className="cards-owner-hint">Searching...</div> : null}
                      {cardSuggestionsOpen && !!cardSuggestions.length ? (
                        <div className="cards-owner-suggestions">
                          {cardSuggestions.map((card) => (
                            <button key={card.card_id} type="button" onClick={() => selectCardSuggestion(card)}>
                              <strong>{card.card_name}</strong>
                              <span>{card.owner_username || card.status || ''}</span>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    <div className="accounts-card-management-actions">
                      <button
                        className="btn btn-primary"
                        type="button"
                        disabled={
                          managingCards ||
                          !selectedCardSuggestion?.card_id ||
                          (
                            activeAccountVendor?.showSecondaryCard !== false &&
                            cardDraft?.primaryCard &&
                            cardDraft?.secondaryCard
                          ) ||
                          (
                            activeAccountVendor?.showSecondaryCard === false &&
                            cardDraft?.primaryCard
                          )
                        }
                        onClick={applyDraftAddCard}
                      >
                        Add Card
                      </button>
                      {cardDraft?.secondaryCard?.card_id && activeAccountVendor?.showSecondaryCard !== false ? (
                        <button
                          className="btn btn-secondary"
                          type="button"
                          disabled={managingCards}
                          onClick={applyDraftSwapCards}
                        >
                          Switch Cards
                        </button>
                      ) : null}
                      {cardDraft?.primaryCard?.card_id ? (
                        <button
                          className="btn btn-secondary"
                          type="button"
                          disabled={managingCards}
                          onClick={() => applyDraftRemoveCard('primary')}
                        >
                          Remove Primary
                        </button>
                      ) : null}
                      {cardDraft?.secondaryCard?.card_id && activeAccountVendor?.showSecondaryCard !== false ? (
                        <button
                          className="btn btn-secondary"
                          type="button"
                          disabled={managingCards}
                          onClick={() => applyDraftRemoveCard('secondary')}
                        >
                          Remove Secondary
                        </button>
                      ) : null}
                      <button
                        className="btn btn-primary"
                        type="button"
                        disabled={managingCards || !cardDraft?.dirty}
                        onClick={handleManageCards}
                      >
                        {managingCards ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>

                    <div className="accounts-card-create">
                      {!showCreateCardForm ? (
                        <button
                          className="btn btn-secondary"
                          type="button"
                          disabled={managingCards || !canCreateCardForDraft()}
                          onClick={() => {
                            setHistoryError('');
                            setShowCreateCardForm(true);
                          }}
                        >
                          Create New Card
                        </button>
                      ) : (
                        <form className="accounts-card-create-form" onSubmit={createAndAssignCard}>
                          <div className="form-group">
                            <label htmlFor="accounts-new-card-name">Card Name</label>
                            <input
                              id="accounts-new-card-name"
                              type="text"
                              value={createCardForm.card_name}
                              onChange={(event) => updateCreateCardForm('card_name', event.target.value)}
                              disabled={creatingCard}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="accounts-new-card-status">Status</label>
                            <input
                              id="accounts-new-card-status"
                              type="text"
                              value={createCardForm.status}
                              onChange={(event) => updateCreateCardForm('status', event.target.value)}
                              disabled={creatingCard}
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="accounts-new-card-expiry">Expiry</label>
                            <input
                              id="accounts-new-card-expiry"
                              type="text"
                              value={createCardForm.expiry}
                              onChange={(event) => updateCreateCardForm('expiry', event.target.value)}
                              disabled={creatingCard}
                              placeholder="MM/YY"
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="accounts-new-card-ccv">CCV</label>
                            <input
                              id="accounts-new-card-ccv"
                              type="text"
                              value={createCardForm.ccv}
                              onChange={(event) => updateCreateCardForm('ccv', event.target.value)}
                              disabled={creatingCard}
                            />
                          </div>
                          <label className="accounts-card-create-checkbox">
                            <input
                              type="checkbox"
                              checked={createCardForm.is_zen_privacy}
                              onChange={(event) => updateCreateCardForm('is_zen_privacy', event.target.checked)}
                              disabled={creatingCard}
                            />
                            <span>Zen/Privacy</span>
                          </label>
                          <div className="accounts-card-create-actions">
                            <button
                              className="btn btn-secondary"
                              type="button"
                              disabled={creatingCard}
                              onClick={() => {
                                setShowCreateCardForm(false);
                                setCreateCardForm(EMPTY_CREATE_CARD_FORM);
                                setHistoryError('');
                              }}
                            >
                              Cancel
                            </button>
                            <button className="btn btn-primary" type="submit" disabled={creatingCard}>
                              {creatingCard ? 'Creating...' : 'Create and Assign'}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </section>
                </>
              ) : null}
            </div>
          </section>
        </div>
      )}

      {cardModal && (
        <div className="venues-modal-backdrop" onMouseDown={closeCardModal}>
          <section className="venues-modal accounts-card-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="venues-modal-header">
              <h2>{cardModal.card.card_name}</h2>
              <button className="venues-modal-close" type="button" onClick={closeCardModal} disabled={savingCardDetails}>
                x
              </button>
            </div>
            <div className="accounts-card-body">
              <div className="accounts-card-detail-grid">
                <span>Status</span>
                <strong>{cardModal.card.status || '-'}</strong>
                <span>Expiry</span>
                <strong>{cardModal.card.expiry || '-'}</strong>
                <span>Zen/Privacy</span>
                <strong><BooleanPill value={cardModal.card.is_zen_privacy} /></strong>
              </div>
              <div className="form-group">
                <label htmlFor="dice-card-details">Details</label>
                <textarea
                  id="dice-card-details"
                  value={cardDetailsDraft}
                  onChange={(event) => setCardDetailsDraft(event.target.value)}
                  placeholder="Apple masked, saved in desktop, or other notes..."
                  disabled={savingCardDetails}
                />
              </div>
            </div>
            <div className="venues-modal-actions">
              <button className="btn btn-secondary" type="button" onClick={closeCardModal} disabled={savingCardDetails}>
                Cancel
              </button>
              <button className="btn btn-primary" type="button" onClick={saveCardDetails} disabled={savingCardDetails}>
                {savingCardDetails ? 'Saving...' : 'Save Details'}
              </button>
            </div>
          </section>
        </div>
      )}

      {cardConflictModal ? (
        <div className="venues-modal-backdrop">
          <section className="venues-modal accounts-card-confirm-modal">
            <div className="venues-modal-header">
              <h2>Shared Card Warning</h2>
            </div>
            <div className="accounts-card-confirm-body">
              <p>{cardConflictModal.message}</p>
              <p>Do you want to proceed?</p>
            </div>
            <div className="venues-modal-actions">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setCardConflictModal(null)}
                disabled={managingCards}
              >
                No
              </button>
              <button
                className="btn btn-primary"
                type="button"
                onClick={confirmManageCards}
                disabled={managingCards}
              >
                {managingCards ? 'Saving...' : 'Yes'}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
};

export default AccountsPage;
